import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Backdrop,
  InputAdornment,
  IconButton,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  AppBar,
  Toolbar,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  SportsScore as SportsIcon,
  EmojiEvents as TrophyIcon,
  GroupAdd as GroupAddIcon,
  HourglassEmpty as HourglassIcon,
  Visibility,
  VisibilityOff,
  Add as AddIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  LocationOn as LocationIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import {
  SeasonRegistrationRequest,
  GearTshirt,
  GearShorts,
  GearSocks,
  GearDesign,
  Gender,
} from "../types";
import { useNavigate } from "react-router-dom";
import CountdownTimer from "../components/CountdownTimer";
import GearDesignPicker from "../components/GearDesignPicker";

const SeasonRegistrationForm: React.FC = () => {
  const navigate = useNavigate();
  const [showAccountQuestion, setShowAccountQuestion] = useState(true);
  const [formData, setFormData] = useState<SeasonRegistrationRequest>({
    playerName: "",
    email: "",
    phone: "",
    nationality: "",
    isDPVRegistered: false,
    wantsGear: false,
    gender: undefined,
    preferredDivisionLevel: "beginner", // Default to beginner
    levelReason: "",
    playtomicLevel: "",
    hasTournamentExperience: false,
    hearAboutUsSource: "",
    hasReferencePlayer: false,
    referencePlayerName: "",
    wantsTrialDay: false,
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);
  const [seasonName, setSeasonName] = useState("Summer 2026");
  const [registrationOpenDate, setRegistrationOpenDate] = useState<Date | null>(
    null,
  );
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [tshirtPrice, setTshirtPrice] = useState<number | null>(null);
  const [shortsPrice, setShortsPrice] = useState<number | null>(null);
  const [kitPrice, setKitPrice] = useState<number | null>(null);
  const [tshirtSizeChartUrl, setTshirtSizeChartUrl] = useState("");
  const [poloSizeChartUrl, setPoloSizeChartUrl] = useState("");
  const [shortsSizeChartUrl, setShortsSizeChartUrl] = useState("");
  const [midSeasonIsOpen, setMidSeasonIsOpen] = useState(false);
  const [midSeasonPrice, setMidSeasonPrice] = useState<number | null>(null);
  const [midSeasonGearEnabled, setMidSeasonGearEnabled] = useState(false);
  const [wantsGear, setWantsGear] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");

  // Account creation fields for new players
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [gearSocks, setGearSocks] = useState<GearSocks[]>([]);
  // Gear arrays for orders
  const [gearTshirts, setGearTshirts] = useState<GearTshirt[]>([]);
  const [gearShorts, setGearShorts] = useState<GearShorts[]>([]);
  const [gearDesigns, setGearDesigns] = useState<GearDesign[]>([]);
  const tshirtDesigns = gearDesigns.filter(
    (d) => (d.category || "tshirt") === "tshirt",
  );
  const poloDesigns = gearDesigns.filter((d) => d.category === "polo");
  const shortsDesigns = gearDesigns.filter((d) => d.category === "shorts");

  // Gear handlers
  const addTshirt = () => {
    setGearTshirts([
      ...gearTshirts,
      { size: "M", shirtType: "tshirt", nameOnShirt: "" },
    ]);
  };

  const removeTshirt = (index: number) => {
    setGearTshirts(gearTshirts.filter((_, i) => i !== index));
  };

  const updateTshirt = (
    index: number,
    field: keyof GearTshirt,
    value: string,
  ) => {
    const updated = [...gearTshirts];
    updated[index] = { ...updated[index], [field]: value };
    // The design catalog is split by shirt type, so a design picked under one
    // type never carries over as a valid designId under the other.
    if (field === "shirtType") {
      delete updated[index].designId;
      delete updated[index].designName;
    }
    setGearTshirts(updated);
  };

  const updateTshirtDesign = (index: number, designId: string) => {
    const design = (
      gearTshirts[index]?.shirtType === "polo" ? poloDesigns : tshirtDesigns
    ).find((d) => d.id === designId);
    const updated = [...gearTshirts];
    updated[index] = {
      ...updated[index],
      designId,
      designName: design?.name,
    };
    setGearTshirts(updated);
  };

  const addShorts = () => {
    setGearShorts([...gearShorts, { size: "M" }]);
    setGearSocks([...gearSocks, { size: "" }]);
  };

  const removeShorts = (index: number) => {
    setGearShorts(gearShorts.filter((_, i) => i !== index));
    setGearSocks(gearSocks.filter((_, i) => i !== index));
  };

  const updateShorts = (index: number, size: string) => {
    const updated = [...gearShorts];
    updated[index] = { ...updated[index], size: size as any };
    setGearShorts(updated);
  };

  const updateShortsDesign = (index: number, designId: string) => {
    const design = shortsDesigns.find((d) => d.id === designId);
    const updated = [...gearShorts];
    updated[index] = {
      ...updated[index],
      designId,
      designName: design?.name,
    };
    setGearShorts(updated);
  };

  const updateSocks = (index: number, size: string) => {
    const updated = [...gearSocks];
    updated[index] = { size };
    setGearSocks(updated);
  };

  // Mid-season takes precedence if both windows happen to be open
  const activePhase: "initial" | "mid_season" = midSeasonIsOpen
    ? "mid_season"
    : "initial";
  const gearAllowed = activePhase !== "mid_season" || midSeasonGearEnabled;

  // Smart pricing: auto-detect kit combos
  const isKitCombo =
    gearTshirts.length > 0 && gearShorts.length > 0 && gearSocks.length > 0;

  const calculateGearTotal = (): number => {
    if (!wantsGear) return 0;
    let total = 0;
    if (isKitCombo) {
      // First set of shirt + shorts + socks = kit price
      total += kitPrice || 0;
      // Extra shirts beyond the first
      const extraShirts = Math.max(0, gearTshirts.length - 1);
      total += extraShirts * (tshirtPrice || 0);
      // Extra shorts beyond the first
      const extraShorts = Math.max(0, gearShorts.length - 1);
      total += extraShorts * (shortsPrice || 0);
    } else {
      // No kit combo: individual pricing
      total += gearTshirts.length * (tshirtPrice || 0);
      total += gearShorts.length * (shortsPrice || 0);
    }
    return total;
  };

  // Compute gearOption for storage based on what's ordered
  const computeGearOption = () => {
    if (!wantsGear) return "none";
    if (isKitCombo) return "full-kit";
    if (gearTshirts.length > 0 && gearShorts.length === 0) return "tshirt-only";
    if (gearShorts.length > 0 && gearTshirts.length === 0) return "shorts-only";
    return "full-kit"; // has both but missing socks - treat as kit
  };

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  useEffect(() => {
    checkRegistrationStatus();
    fetchGearDesigns();
  }, []);

  const fetchGearDesigns = async () => {
    try {
      const response = await apiRequest(
        "/api/season-registration/gear-designs",
      );
      if (response.ok) {
        setGearDesigns(await response.json());
      }
    } catch (err) {
      console.error("Failed to fetch gear designs:", err);
    }
  };

  const checkRegistrationStatus = async () => {
    try {
      const response = await apiRequest("/api/season-registration/status");
      if (response.ok) {
        const data = await response.json();
        setIsRegistrationOpen(data.isOpen || data.midSeasonIsOpen);
        setSeasonName(data.seasonName || "Summer 2026");
        if (data.registrationOpenDate) {
          setRegistrationOpenDate(new Date(data.registrationOpenDate));
        }
        if (data.estimatedPrice) {
          setEstimatedPrice(data.estimatedPrice);
        }
        if (data.tshirtPrice) {
          setTshirtPrice(data.tshirtPrice);
        }
        if (data.shortsPrice) {
          setShortsPrice(data.shortsPrice);
        }
        if (data.kitPrice) {
          setKitPrice(data.kitPrice);
        }
        setTshirtSizeChartUrl(data.tshirtSizeChartUrl || "");
        setPoloSizeChartUrl(data.poloSizeChartUrl || "");
        setShortsSizeChartUrl(data.shortsSizeChartUrl || "");
        setMidSeasonIsOpen(data.midSeasonIsOpen || false);
        setMidSeasonPrice(data.midSeasonPrice ?? null);
        setMidSeasonGearEnabled(data.midSeasonGearEnabled || false);
      }
    } catch (err) {
      console.error("Failed to check registration status:", err);
      // Default to closed on error
      setIsRegistrationOpen(false);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError("Please enter a valid email address");
        setLoading(false);
        return;
      }

      // Validate required fields
      if (
        !formData.playerName.trim() ||
        !formData.email.trim() ||
        !formData.phone.trim() ||
        !formData.nationality.trim()
      ) {
        setError("Please fill in all required fields");
        setLoading(false);
        return;
      }

      // Validate level reason: required when a higher level is selected
      if (
        ["intermediate", "advanced", "pro"].includes(
          formData.preferredDivisionLevel || "",
        ) &&
        !formData.levelReason?.trim()
      ) {
        setError(
          "Please explain why you think you should be placed at this level",
        );
        setLoading(false);
        return;
      }

      // Validate reference player name if applicable
      if (formData.hasReferencePlayer && !formData.referencePlayerName?.trim()) {
        setError("Please enter the name of the SPL player who knows your level");
        setLoading(false);
        return;
      }

      // Validate username
      if (!username.trim()) {
        setError("Username is required");
        setLoading(false);
        return;
      }

      if (username.length < 3) {
        setError("Username must be at least 3 characters");
        setLoading(false);
        return;
      }

      // Check for invalid characters in username
      const usernameRegex = /^[a-zA-Z0-9_-]+$/;
      if (!usernameRegex.test(username)) {
        setError(
          "Username can only contain letters, numbers, hyphens, and underscores (no spaces or special characters)",
        );
        setLoading(false);
        return;
      }

      // Validate password
      if (!password) {
        setError("Password is required");
        setLoading(false);
        return;
      }

      if (password.length < 6) {
        setError("Password must be at least 6 characters");
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Validate gear: must have at least one item if gear is requested
      if (wantsGear) {
        if (gearTshirts.length === 0 && gearShorts.length === 0) {
          setError("Please add at least one gear item (shirt or shorts)");
          setLoading(false);
          return;
        }
        // A design must be picked for every gear item
        if (
          gearTshirts.some((t) => !t.designId) ||
          gearShorts.some((s) => !s.designId)
        ) {
          setError("Please choose a design for each gear item");
          setLoading(false);
          return;
        }
        // If shorts are ordered, shoe sizes for socks are required
        if (gearSocks.some((s) => !s.size)) {
          setError(
            "Please enter shoe size for all socks (included with shorts)",
          );
          setLoading(false);
          return;
        }
      }

      // Validate privacy and cancellation policy consent
      if (!privacyConsent) {
        setError("You must accept the Privacy Policy to continue");
        setLoading(false);
        return;
      }

      // Build request payload
      const gearTotalCost = calculateGearTotal();
      const gearOption = computeGearOption();
      const registrationPayload: SeasonRegistrationRequest = {
        ...formData,
        wantsGear,
        gearOption: gearOption as any,
        gearTotalCost,
        username: username.trim(),
        password: password,
        gearTshirts:
          wantsGear && gearTshirts.length > 0 ? gearTshirts : undefined,
        gearShorts: wantsGear && gearShorts.length > 0 ? gearShorts : undefined,
        gearSocks: wantsGear && gearSocks.length > 0 ? gearSocks : undefined,
        voucherCode: voucherCode.trim() || undefined,
        gender: formData.gender || undefined,
        levelReason: formData.levelReason?.trim() || undefined,
        playtomicLevel: formData.playtomicLevel?.trim() || undefined,
        hearAboutUsSource: formData.hearAboutUsSource?.trim() || undefined,
        referencePlayerName: formData.hasReferencePlayer
          ? formData.referencePlayerName?.trim() || undefined
          : undefined,
      };

      const response = await apiRequest("/api/season-registration/submit", {
        method: "POST",
        body: JSON.stringify(registrationPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit registration");
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(
        err.message || "Failed to submit registration. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Card
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              borderRadius: 4,
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                mb: 3,
                animation: "bounce 1s ease-in-out",
                "@keyframes bounce": {
                  "0%, 100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-20px)" },
                },
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: "white" }} />
            </Box>
            <Typography
              variant="h3"
              gutterBottom
              fontWeight="bold"
              color="primary"
            >
              Thank You! 🙏
            </Typography>
            <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
              Registration Received
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Thank you <strong>{formData.playerName}</strong> for your interest
              in joining the Stuttgart Padel League!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "left" }}>
              We are now gathering all interested players. We will then
              distribute the players into their corresponding divisions and
              reach out to each player at their provided email.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, textAlign: "left" }}>
              Yours is: <strong>{formData.email}</strong>
              <br />
              <Typography component="span" variant="caption" color="text.secondary">
                (If the email is wrong, please reach out to us)
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "left" }}>
              We will reach out to you informing you of the division you have
              been placed in, whether you were selected for the trial day, and
              the payment info.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "left" }}>
              After payment, you are confirmed in the SPL season — your account
              will be activated on the site and you will be added to the
              WhatsApp group.
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              If you have any questions, reach out to us:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WhatsAppIcon sx={{ color: "success.main" }} />
                <Typography variant="body2">+49 176 20193968</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon color="action" />
                <Typography variant="body2">stuttgartpadel@gmail.com</Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  playerName: "",
                  email: "",
                  phone: "",
                  nationality: "",
                  isDPVRegistered: false,
                  wantsGear: false,
                  preferredDivisionLevel: "beginner",
                  wantsTrialDay: false,
                });
                setGearTshirts([]);
                setGearShorts([]);
                setVoucherCode("");
              }}
              sx={{
                mt: 4,
                py: 1.5,
                px: 4,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              Register Another Player
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  if (checkingStatus) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: "white" }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Season Registration
          </Typography>
          <Button
            color="inherit"
            startIcon={<PublicIcon />}
            onClick={() => navigate("/")}
          >
            Home
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          bgcolor: "#f5f5f5",
          py: { xs: 2, sm: 4, md: 6 },
          position: "relative",
        }}
      >
        {/* Closed Registration Overlay */}
        {!isRegistrationOpen && (
          <Backdrop
            open={true}
            sx={{
              zIndex: 1300,
              background: "rgba(0, 0, 0, 0.8)",
              backdropFilter: "blur(10px)",
              overflowY: "auto", // Allow scrolling on mobile if needed
            }}
          >
            <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 3 } }}>
              <Card
                sx={{
                  textAlign: "center",
                  py: { xs: 3, sm: 5, md: 6 },
                  px: { xs: 2, sm: 3, md: 4 },
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(30, 30, 30, 0.95)"
                      : "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                  borderRadius: 4,
                  maxHeight: { xs: "95vh", sm: "auto" }, // Prevent overflow on mobile
                  overflowY: { xs: "auto", sm: "visible" }, // Enable scrolling on mobile if needed
                }}
              >
                <Box
                  sx={{
                    width: { xs: 80, sm: 100, md: 120 },
                    height: { xs: 80, sm: 100, md: 120 },
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    mb: { xs: 2, sm: 3 },
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { transform: "scale(1)", opacity: 1 },
                      "50%": { transform: "scale(1.1)", opacity: 0.8 },
                    },
                  }}
                >
                  <HourglassIcon
                    sx={{
                      fontSize: { xs: 50, sm: 60, md: 70 },
                      color: "white",
                    }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  gutterBottom
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                  }}
                >
                  Coming Soon! 🎾
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.5rem" },
                    fontWeight: 500,
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[300]
                        : theme.palette.text.secondary,
                  }}
                >
                  Registration for {seasonName}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    fontSize: { xs: "0.95rem", sm: "1.1rem" },
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[100]
                        : theme.palette.text.primary,
                  }}
                >
                  We're preparing something amazing for the next season of
                  Stuttgart Padel League!
                </Typography>
                {registrationOpenDate &&
                new Date(registrationOpenDate) > new Date() ? (
                  <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 2,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        fontWeight: 600,
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                      }}
                    >
                      Registration opens on:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.95rem", sm: "1.25rem" },
                        fontWeight: "bold",
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[200]
                            : theme.palette.text.primary,
                      }}
                    >
                      {new Date(registrationOpenDate).toLocaleString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 2,
                        p: { xs: 2, sm: 3 },
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <CountdownTimer
                        targetDate={registrationOpenDate}
                        onComplete={checkRegistrationStatus}
                        prefix="Registration opens in:"
                        variant="minimal"
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      mb: { xs: 3, sm: 4 },
                      fontSize: { xs: "0.875rem", sm: "0.875rem" },
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[400]
                          : theme.palette.text.secondary,
                    }}
                  >
                    Registration will open very soon. Stay tuned and get ready
                    to join the most exciting padel community in Stuttgart! 🚀
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1.5, sm: 2 },
                    justifyContent: "center",
                    flexWrap: "wrap",
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <TrophyIcon
                      sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: "#667eea",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[300]
                            : "inherit",
                      }}
                    >
                      Epic Battles
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <SportsIcon
                      sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: "#764ba2",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[300]
                            : "inherit",
                      }}
                    >
                      New Challenges
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <GroupAddIcon
                      sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: "#f093fb",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[300]
                            : "inherit",
                      }}
                    >
                      Amazing People
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <LocationIcon
                      sx={{
                        fontSize: { xs: 32, sm: 40 },
                        color: "#4facfe",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        color: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[300]
                            : "inherit",
                      }}
                    >
                      More Locations
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    color: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[400]
                        : theme.palette.text.secondary,
                  }}
                >
                  Questions? Reach out to us at{" "}
                  <Box
                    component="a"
                    href="mailto:stuttgartpadel@gmail.com"
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark"
                          ? theme.palette.primary.light
                          : "#667eea",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                  >
                    stuttgartpadel@gmail.com
                  </Box>
                </Typography>
                <Box
                  sx={{
                    mt: { xs: 3, sm: 4 },
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/")}
                    sx={{
                      py: { xs: 1.2, sm: 1.5 },
                      px: { xs: 3, sm: 4 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    ← Back to Home
                  </Button>
                </Box>
              </Card>
            </Container>
          </Backdrop>
        )}

        <Container maxWidth="md">
          {showAccountQuestion ? (
            /* Account Question Screen */
            <Card
              sx={{
                maxWidth: 600,
                mx: "auto",
                mt: 8,
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                borderRadius: 4,
              }}
            >
              <CardContent sx={{ py: 6, px: 4 }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      mb: 3,
                    }}
                  >
                    <SportsIcon sx={{ fontSize: 60, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="primary"
                    gutterBottom
                  >
                    Welcome! 🎾
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Register for Stuttgart Padel League - {seasonName}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Before we begin, do you already have a player account on the
                    SPL website?
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate("/player/login")}
                    sx={{
                      py: 2,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5568d3 0%, #6a4293 100%)",
                      },
                    }}
                  >
                    ✅ Yes, I have an account - Login
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setShowAccountQuestion(false)}
                    sx={{
                      py: 2,
                      borderColor: "#667eea",
                      color: "#667eea",
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      "&:hover": {
                        borderColor: "#5568d3",
                        backgroundColor: "rgba(102, 126, 234, 0.1)",
                      },
                    }}
                  >
                    ❌ No, I'm a new player
                  </Button>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 4, textAlign: "center" }}
                >
                  If you have an account, please login first. Your registration
                  will be automatically filled with your profile information.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            /* Registration Form */
            <>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    px: 4,
                    py: 2,
                    borderRadius: 4,
                  }}
                >
                  <SportsIcon sx={{ fontSize: 50, color: "white" }} />
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{
                      color: "white",
                    }}
                  >
                    Join the Action! 🎾
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: "text.primary",
                    fontWeight: 500,
                  }}
                >
                  {activePhase === "mid_season"
                    ? `Mid-Season Registration - ${seasonName}`
                    : "Register for Stuttgart Padel League - Next Season"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    mt: 1,
                    fontSize: "1.1rem",
                  }}
                >
                  Be part of Stuttgart's most exciting padel community!
                </Typography>
              </Box>

              <Paper
                elevation={10}
                sx={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                {/* Decorative header */}
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    py: 3,
                    px: 4,
                    color: "white",
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Player Information
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Fill in your details to secure your spot in the league
                  </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                  <form onSubmit={handleSubmit}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      {error && (
                        <Alert
                          ref={errorRef}
                          severity="error"
                          onClose={() => setError(null)}
                          sx={{ borderRadius: 2 }}
                        >
                          {error}
                        </Alert>
                      )}

                      <TextField
                        label="Full Name"
                        name="playerName"
                        value={formData.playerName}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <TextField
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <TextField
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <TextField
                        label="Nationality"
                        name="nationality"
                        value={formData.nationality}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        helperText="🏳️ This will be used as a flag on your shirt if you order gear"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <FormControl fullWidth>
                        <InputLabel id="gender-select-label">
                          Gender
                        </InputLabel>
                        <Select
                          labelId="gender-select-label"
                          label="Gender"
                          value={formData.gender || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gender: e.target.value as Gender,
                            })
                          }
                          sx={{
                            borderRadius: 2,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="Man">Man</MenuItem>
                          <MenuItem value="Woman">Woman</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>

                      <Divider sx={{ my: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: "bold" }}
                        >
                          Division Level Preference
                        </Typography>
                      </Divider>

                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          New players normally enter the division with level{" "}
                          <strong>Beginner</strong> or{" "}
                          <strong>Beginner+</strong>.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Note: This doesn't guarantee you being placed in that
                          division - final placement is determined by
                          management.
                        </Typography>
                      </Alert>

                      <FormControl fullWidth required>
                        <InputLabel>Which level would you prefer?</InputLabel>
                        <Select
                          name="preferredDivisionLevel"
                          value={formData.preferredDivisionLevel || "beginner"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              preferredDivisionLevel: e.target.value as
                                | "beginner"
                                | "beginner+"
                                | "intermediate"
                                | "advanced"
                                | "pro",
                              // Clear the reason if the player switches back to a level that doesn't require one
                              levelReason: ["intermediate", "advanced", "pro"].includes(
                                e.target.value,
                              )
                                ? formData.levelReason
                                : "",
                            })
                          }
                          label="Which level would you prefer?"
                          sx={{
                            borderRadius: 2,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="beginner">🟢 Beginner</MenuItem>
                          <MenuItem value="beginner+">🔵 Beginner+</MenuItem>
                          <MenuItem value="intermediate">🟡 Intermediate</MenuItem>
                          <MenuItem value="advanced">🟠 Advanced</MenuItem>
                          <MenuItem value="pro">🔴 Pro</MenuItem>
                        </Select>
                      </FormControl>

                      {["intermediate", "advanced", "pro"].includes(
                        formData.preferredDivisionLevel || "",
                      ) && (
                        <TextField
                          label={`Why do you think you should be placed at ${formData.preferredDivisionLevel} level?`}
                          value={formData.levelReason || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              levelReason: e.target.value,
                            })
                          }
                          required
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          helperText="Please explain your padel experience/background that supports this level"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#667eea",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#667eea",
                              },
                            },
                          }}
                        />
                      )}

                      <TextField
                        label="Playtomic Level (optional)"
                        value={formData.playtomicLevel || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            playtomicLevel: e.target.value,
                          })
                        }
                        fullWidth
                        variant="outlined"
                        helperText="e.g., 3.5 - if you have a Playtomic profile with a level"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          Did you participate in any tournaments?
                        </Typography>
                        <ToggleButtonGroup
                          value={
                            formData.hasTournamentExperience ? "yes" : "no"
                          }
                          exclusive
                          onChange={(_, val) => {
                            if (val)
                              setFormData({
                                ...formData,
                                hasTournamentExperience: val === "yes",
                              });
                          }}
                          size="small"
                        >
                          <ToggleButton
                            value="yes"
                            sx={{ px: 3, textTransform: "none", fontWeight: 600 }}
                          >
                            Yes
                          </ToggleButton>
                          <ToggleButton
                            value="no"
                            sx={{ px: 3, textTransform: "none", fontWeight: 600 }}
                          >
                            No
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      <TextField
                        label="Where did you hear about the Stuttgart Padel League from?"
                        value={formData.hearAboutUsSource || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hearAboutUsSource: e.target.value,
                          })
                        }
                        fullWidth
                        variant="outlined"
                        placeholder="e.g., Instagram, a friend, a padel club, ..."
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          Do you play regularly with a player from SPL that we
                          can ask your level from?
                        </Typography>
                        <ToggleButtonGroup
                          value={formData.hasReferencePlayer ? "yes" : "no"}
                          exclusive
                          onChange={(_, val) => {
                            if (val)
                              setFormData({
                                ...formData,
                                hasReferencePlayer: val === "yes",
                                referencePlayerName:
                                  val === "yes"
                                    ? formData.referencePlayerName
                                    : "",
                              });
                          }}
                          size="small"
                        >
                          <ToggleButton
                            value="yes"
                            sx={{ px: 3, textTransform: "none", fontWeight: 600 }}
                          >
                            Yes
                          </ToggleButton>
                          <ToggleButton
                            value="no"
                            sx={{ px: 3, textTransform: "none", fontWeight: 600 }}
                          >
                            No
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      {formData.hasReferencePlayer && (
                        <TextField
                          label="Name of the SPL player who can vouch for your level"
                          value={formData.referencePlayerName || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              referencePlayerName: e.target.value,
                            })
                          }
                          required
                          fullWidth
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              "&:hover fieldset": {
                                borderColor: "#667eea",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#667eea",
                              },
                            },
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(118,75,162,0.08)",
                          borderRadius: 2,
                          border: "2px solid rgba(118,75,162,0.2)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ mb: 1 }}
                        >
                          🏆 If we are still not sure about your level, are
                          you okay to participate in a trial day so that we
                          can evaluate your level?
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          If not, then we evaluate the level based on our
                          knowledge of you.
                        </Typography>
                        <Typography
                          variant="caption"
                          color="warning.main"
                          display="block"
                          sx={{ mb: 1.5, fontStyle: "italic" }}
                        >
                          Note: Trial days are counted from your contingent
                          of paid league days.
                        </Typography>
                        <ToggleButtonGroup
                          value={formData.wantsTrialDay ? "yes" : "no"}
                          exclusive
                          onChange={(_, val) => {
                            if (val)
                              setFormData({
                                ...formData,
                                wantsTrialDay: val === "yes",
                              });
                          }}
                          size="small"
                        >
                          <ToggleButton
                            value="yes"
                            sx={{
                              px: 3,
                              textTransform: "none",
                              fontWeight: 600,
                              "&.Mui-selected": {
                                color: "#764ba2",
                                bgcolor: "rgba(118,75,162,0.15)",
                              },
                            }}
                          >
                            Yes
                          </ToggleButton>
                          <ToggleButton
                            value="no"
                            sx={{
                              px: 3,
                              textTransform: "none",
                              fontWeight: 600,
                              "&.Mui-selected": {
                                color: "#764ba2",
                                bgcolor: "rgba(118,75,162,0.15)",
                              },
                            }}
                          >
                            No
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      <Divider sx={{ my: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: "bold" }}
                        >
                          Account Creation
                        </Typography>
                      </Divider>

                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Create your account now. Once approved by an admin,
                        you'll be able to login and access all features!
                      </Alert>

                      <TextField
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        helperText="At least 3 characters, letters, numbers, hyphens, and underscores only"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        helperText="At least 6 characters"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <TextField
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        error={
                          confirmPassword.length > 0 &&
                          password !== confirmPassword
                        }
                        helperText={
                          confirmPassword.length > 0 &&
                          password !== confirmPassword
                            ? "Passwords do not match"
                            : "Re-enter your password"
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      <Divider sx={{ my: 2 }} />

                      <Box
                        sx={{
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid #e0e0e0",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isDPVRegistered"
                              checked={formData.isDPVRegistered}
                              onChange={handleChange}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              I am registered with the{" "}
                              <strong>Deutscher Padel Verband (DPV)</strong>
                            </Typography>
                          }
                        />

                        {/* Gear Selection */}
                        {gearAllowed && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={wantsGear}
                                onChange={(e) => {
                                  setWantsGear(e.target.checked);
                                  if (
                                    e.target.checked &&
                                    gearTshirts.length === 0
                                  ) {
                                    setGearTshirts([
                                      {
                                        size: "M",
                                        shirtType: "tshirt",
                                        nameOnShirt: "",
                                      },
                                    ]);
                                  }
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  I want to order official{" "}
                                  <strong>Stuttgart Padel League gear</strong>{" "}
                                  🎽
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  Note: After the issues we had in the Summer
                                  Season with the T-shirt orders, we decided
                                  to go back to the old sponsor, as the
                                  quality and delivery time were guaranteed
                                  and we didn't have the printing problems or
                                  sizing problems.
                                </Typography>
                              </Box>
                            }
                            sx={{ alignItems: "flex-start" }}
                          />
                        )}
                      </Box>

                      {/* Gear items section */}
                      {gearAllowed && wantsGear && (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            bgcolor: "#fafafa",
                          }}
                        >
                          <Alert severity="info" sx={{ mb: 2 }}>
                            💡 <strong>Smart pricing:</strong> Order a shirt +
                            shorts + socks together and save! Kit price:{" "}
                            {kitPrice ? `${kitPrice}€` : "TBD"} vs individual
                            shirt ({tshirtPrice ? `${tshirtPrice}€` : "TBD"}) +
                            shorts+socks (
                            {shortsPrice ? `${shortsPrice}€` : "TBD"}).
                          </Alert>

                          {/* Shirts section */}
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                            sx={{ mb: 1 }}
                          >
                            👕 Shirts
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Choose T-Shirt or Polo for each item
                          </Typography>

                          {gearTshirts.map((tshirt, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                border: "1px solid rgba(118,75,162,0.3)",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <ToggleButtonGroup
                                  value={tshirt.shirtType || "tshirt"}
                                  exclusive
                                  onChange={(_, val) => {
                                    if (val)
                                      updateTshirt(index, "shirtType", val);
                                  }}
                                  size="small"
                                >
                                  <ToggleButton
                                    value="tshirt"
                                    sx={{
                                      px: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    👕 T-Shirt
                                  </ToggleButton>
                                  <ToggleButton
                                    value="polo"
                                    sx={{
                                      px: 2,
                                      textTransform: "none",
                                      fontWeight: 600,
                                    }}
                                  >
                                    🏇 Polo
                                  </ToggleButton>
                                </ToggleButtonGroup>
                                <IconButton
                                  onClick={() => removeTshirt(index)}
                                  color="error"
                                  disabled={gearTshirts.length === 1}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <GearDesignPicker
                                  designs={
                                    tshirt.shirtType === "polo"
                                      ? poloDesigns
                                      : tshirtDesigns
                                  }
                                  selectedId={tshirt.designId}
                                  onSelect={(designId) =>
                                    updateTshirtDesign(index, designId)
                                  }
                                  label="Design"
                                  sizeChartUrl={
                                    tshirt.shirtType === "polo"
                                      ? poloSizeChartUrl
                                      : tshirtSizeChartUrl
                                  }
                                />
                                <FormControl sx={{ minWidth: 100 }}>
                                  <InputLabel>Size</InputLabel>
                                  <Select
                                    value={tshirt.size}
                                    onChange={(e) =>
                                      updateTshirt(
                                        index,
                                        "size",
                                        e.target.value,
                                      )
                                    }
                                    label="Size"
                                    size="small"
                                  >
                                    <MenuItem value="S">S</MenuItem>
                                    <MenuItem value="M">M</MenuItem>
                                    <MenuItem value="L">L</MenuItem>
                                    <MenuItem value="XL">XL</MenuItem>
                                    <MenuItem value="XXL">XXL</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  fullWidth
                                  label="Name on Shirt (Optional)"
                                  value={tshirt.nameOnShirt || ""}
                                  onChange={(e) =>
                                    updateTshirt(
                                      index,
                                      "nameOnShirt",
                                      e.target.value,
                                    )
                                  }
                                  size="small"
                                  inputProps={{ maxLength: 20 }}
                                />
                              </Box>
                            </Box>
                          ))}

                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addTshirt}
                            size="small"
                            sx={{ mb: 3 }}
                          >
                            Add Another Shirt
                          </Button>

                          <Divider sx={{ my: 2 }} />

                          {/* Shorts section */}
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                            sx={{ mb: 1 }}
                          >
                            🩳 Shorts/Skirt + Socks (optional)
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Each shorts comes with a pair of socks. Add shorts
                            to combine with a shirt for kit pricing.
                          </Typography>

                          {gearShorts.length === 0 ? (
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={addShorts}
                              size="small"
                              sx={{ mb: 3 }}
                            >
                              Add Shorts/Skirt + Socks
                            </Button>
                          ) : (
                            <>
                              {gearShorts.map((shorts, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 2,
                                    p: 1.5,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                    border: "1px solid rgba(118,75,162,0.3)",
                                  }}
                                >
                                  <GearDesignPicker
                                    designs={shortsDesigns}
                                    selectedId={shorts.designId}
                                    onSelect={(designId) =>
                                      updateShortsDesign(index, designId)
                                    }
                                    label="Design"
                                    sizeChartUrl={shortsSizeChartUrl}
                                  />
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      mb: 1,
                                      mt: 1,
                                    }}
                                  >
                                    <FormControl sx={{ flex: 1 }}>
                                      <InputLabel>Shorts Size</InputLabel>
                                      <Select
                                        value={shorts.size}
                                        onChange={(e) =>
                                          updateShorts(index, e.target.value)
                                        }
                                        label="Shorts Size"
                                        size="small"
                                      >
                                        <MenuItem value="S">S - Small</MenuItem>
                                        <MenuItem value="M">
                                          M - Medium
                                        </MenuItem>
                                        <MenuItem value="L">L - Large</MenuItem>
                                        <MenuItem value="XL">
                                          XL - Extra Large
                                        </MenuItem>
                                        <MenuItem value="XXL">
                                          XXL - 2X Large
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                    <IconButton
                                      onClick={() => removeShorts(index)}
                                      color="error"
                                      size="small"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                  <TextField
                                    fullWidth
                                    label="🧦 Socks - Shoe Size"
                                    value={gearSocks[index]?.size || ""}
                                    onChange={(e) =>
                                      updateSocks(index, e.target.value)
                                    }
                                    size="small"
                                    placeholder="e.g., 42"
                                    inputProps={{
                                      maxLength: 2,
                                      pattern: "[0-9]*",
                                    }}
                                    helperText="Included with shorts"
                                  />
                                </Box>
                              ))}

                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addShorts}
                                size="small"
                                sx={{ mb: 3 }}
                              >
                                Add Another Shorts/Skirt + Socks
                              </Button>
                            </>
                          )}

                          {/* Smart pricing display */}
                          {(gearTshirts.length > 0 ||
                            gearShorts.length > 0) && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              {isKitCombo && (
                                <Alert severity="success" sx={{ mb: 1 }}>
                                  🎉 <strong>Kit discount applied!</strong> Your
                                  first shirt + shorts + socks are bundled at
                                  the kit price ({kitPrice}€).
                                  {(gearTshirts.length > 1 ||
                                    gearShorts.length > 1) && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {gearTshirts.length > 1 &&
                                        `+ ${gearTshirts.length - 1} extra shirt(s) at ${tshirtPrice}€ each`}
                                      {gearTshirts.length > 1 &&
                                        gearShorts.length > 1 &&
                                        " | "}
                                      {gearShorts.length > 1 &&
                                        `+ ${gearShorts.length - 1} extra shorts at ${shortsPrice}€ each`}
                                    </Typography>
                                  )}
                                </Alert>
                              )}

                              <Alert severity="info">
                                💰 Estimated Gear Total:{" "}
                                <strong>{calculateGearTotal()}€</strong>
                              </Alert>
                            </>
                          )}
                        </Box>
                      )}

                      <TextField
                        label="Voucher Code (Optional)"
                        value={voucherCode}
                        onChange={(e) =>
                          setVoucherCode(e.target.value.toUpperCase())
                        }
                        fullWidth
                        variant="outlined"
                        helperText="Have a discount code? Enter it here - it will be applied once your registration is approved"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      />

                      {/* Estimated Price Information */}
                      {activePhase === "mid_season" ? (
                        midSeasonPrice && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                              bgcolor: "#fafafa",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              gutterBottom
                            >
                              💶 Price Information
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.primary" }}
                            >
                              Price for joining the remaining half of this
                              season is <strong>{midSeasonPrice} Euro</strong>{" "}
                              . This should be paid first after we
                              approve the registration. More info will be sent
                              to you on your email including payment info.
                            </Typography>
                          </Box>
                        )
                      ) : (
                        estimatedPrice && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: 2,
                              border: "1px solid #e0e0e0",
                              bgcolor: "#fafafa",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              gutterBottom
                            >
                              💶 Price Information
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.primary" }}
                            >
                              Estimated price for this season will be around{" "}
                              <strong>{estimatedPrice} Euro</strong> incl. 5 free
                              league days. This should be paid
                              first after we approve the registration. More info
                              will be sent to you on your email including payment
                              info and the exact price.
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1,
                                fontStyle: "italic",
                                color: "text.secondary",
                              }}
                            >
                              The price might change slightly depending on the
                              number of registrations we receive for next season.
                            </Typography>
                          </Box>
                        )
                      )}

                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <FormControlLabel
                          required
                          control={
                            <Checkbox
                              checked={privacyConsent}
                              onChange={(e) =>
                                setPrivacyConsent(e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="body2">
                              I agree to the{" "}
                              <a
                                href="/privacy"
                                target="_blank"
                                style={{ color: "#1976d2", fontWeight: "bold" }}
                              >
                                Privacy Policy
                              </a>{" "}
                              and consent to the processing of my personal data
                              *
                            </Typography>
                          }
                        />
                      </Box>

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          mt: 2,
                          py: 2,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Join the League! 🚀"
                        )}
                      </Button>
                    </Box>
                  </form>
                </CardContent>
              </Paper>

              <Typography
                variant="body2"
                textAlign="center"
                sx={{
                  mt: 3,
                  color: "text.secondary",
                }}
              >
                Questions? Contact us at{" "}
                <a
                  href="mailto:stuttgartpadel@gmail.com"
                  style={{
                    color: "#667eea",
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  stuttgartpadel@gmail.com
                </a>
              </Typography>
            </>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default SeasonRegistrationForm;
